import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { PATH } from '../../constants/router'
import { useAppParamsContext } from '../../context/AppParamsContext'
import appUserService from '../../services/appUserService'
import { setPartnerVehicleResult } from '../../helper/partnerSessionStore'
import LayoutPartner from '../../components/FeaturesComponent/layoutPartner'
import HeaderPartner from '../../components/FeaturesComponent/headerPartner'
import AutomatedTrafficFineNotificationErrorModal from '../../components/FeaturesComponent/automatedTrafficFineNotificationErrorModal'
import BottomBenefitsSection from './components/BottomBenefitsSection'
import EmptyRecentCard from './components/EmptyRecentCard'
import ExpandedUtilitiesSection from './components/ExpandedUtilitiesSection'
import RecentVehicleList from './components/RecentVehicleList'
import VehicleSearchCard from './components/VehicleSearchCard'
// import ViettelMoneyHeader from './components/ViettelMoneyHeader'
import { smartParseParam } from '../../helper/params'
import { normalizePlate, sanitizePlateInput, validatePlateNumber } from '../../helper/validatorPlateNumber'
import { loadHomeCache, saveHomeCache } from './utils/homeCache'
import { validatePhone } from './utils/validatePhone'
import trafficFineBenefitCar from '../../assets/images/traffic_fine_benefit_car.png'
import trafficFineBgHero from '../../assets/images/traffic_fine_bg_hero.jpg'
import trafficFineUtilityPlaceholder from '../../assets/images/traffic_fine_utility_placeholder.png'
import vehicleCar from '../../assets/images/vehicle_car.png'
import vehicleElectricMotorbike from '../../assets/images/vehicle_electric_motorbike.png'
import vehicleMotorbike from '../../assets/images/vehicle_motorbike.png'
import './index.scss'

const RECENT_PLATES_STORAGE_KEY = 'violation_lookup:recent_plates:v1'
const VTM_VEHICLE_MAP_KEY = 'violation_lookup:registered_vehicles:v1'
const VTM_USER_STORAGE_KEY = 'violation_lookup:user_session:v1'
const PHONE_STORAGE_KEY = 'partnerMiniApp:phoneNumber:v1'
const SHOW_PHONE_INPUT_KEY = 'showPhoneInput'
const DEFAULT_PASSWORD = '123456'
const DEFAULT_PLATE_COLOR = 'WHITE'

const HAS_CRIMINAL_STATUS = {
  NO: 0,
  YES: 1,
  API_FAIL: 2
}

const ERROR_MESSAGES = {
  INVALID_PLATE_NUMBER: { message: 'Biển số không hợp lệ' },
  INVALID_VEHICLE_CERTIFICATE: { message: 'Số seri không hợp lệ' },
  VEHICLE_NOT_FOUND: { message: 'Không tìm thấy thông tin phương tiện' },
  REQUEST_LIMIT_EXCEEDED: {
    title: 'Đã đạt giới hạn tra cứu',
    message: 'Bạn đã thực hiện quá số lần tra cứu trong ngày.\nVui lòng quay lại vào hôm sau nhé!'
  }
}

const VEHICLE_SUBTYPE = {
  OTO: 1,
  MAYDIEN: 32,
  XEMAY: 30
}

const VEHICLE_TYPE = {
  OTO: 1,
  MAYDIEN: 40,
  XEMAY: 30
}

const VehicleIcons = {
  xemay: vehicleMotorbike,
  oto: vehicleCar,
  maydien: vehicleElectricMotorbike
}

const VehicleTypeIconMap = {
  1: vehicleCar,
  30: vehicleMotorbike,
  40: vehicleElectricMotorbike
}

const VehicleTypesByValue = {
  1: 'Ô tô',
  30: 'Xe máy',
  40: 'Xe máy điện'
}

const VehicleTypes = {
  xemay: { value: 30, label: 'Xe máy' },
  oto: { value: 1, label: 'Ô tô' },
  maydien: { value: 40, label: 'Xe máy điện' }
}

function digitsOnly(v) {
  return String(v || '').replace(/[^\d]/g, '')
}

function safeJsonParse(str, fallback) {
  try {
    return JSON.parse(str)
  } catch {
    return fallback
  }
}

function loadUserData() {
  const obj = safeJsonParse(localStorage.getItem(VTM_USER_STORAGE_KEY), null)
  if (!obj || !obj.token || !obj.appUserId) return null
  return obj
}

function saveUserData(data) {
  localStorage.setItem(VTM_USER_STORAGE_KEY, JSON.stringify(data))
}

function clearAllViolationLookupData() {
  localStorage.removeItem(VTM_USER_STORAGE_KEY)
  localStorage.removeItem(RECENT_PLATES_STORAGE_KEY)
  localStorage.removeItem(VTM_VEHICLE_MAP_KEY)
}

function getUsernamePrefix(search) {
  const fromSession = sessionStorage.getItem('usernamePrefix')
  if (fromSession) return fromSession

  const params = new URLSearchParams(search || '')
  const fromUrl = params.get('usernamePrefix')
  if (fromUrl) return fromUrl

  return process.env.REACT_APP_USERNAME_PREFIX || 'TAMOVE'
}

function paramsToObject(sp) {
  const obj = {}
  for (const [k, v] of sp.entries()) obj[k] = v
  return obj
}

function getActiveNotifyPkgByIndex(vehicle) {
  const idxRaw = vehicle?.activeNotifyPackageIndex
  const idx = idxRaw === null || idxRaw === undefined || idxRaw === '' ? null : Number(idxRaw)
  const pkgs = Array.isArray(vehicle?.customerSchedule) ? vehicle.customerSchedule : []
  return Number.isInteger(idx) && idx >= 0 && idx < pkgs.length ? pkgs[idx] : null
}

function mapVehicleTypeToSubType(vehicleType) {
  const vt = Number(vehicleType)
  if (vt === VEHICLE_TYPE.OTO) return VEHICLE_SUBTYPE.OTO
  if (vt === VEHICLE_TYPE.MAYDIEN) return VEHICLE_SUBTYPE.MAYDIEN
  return VEHICLE_SUBTYPE.XEMAY
}

function subTypeToVehicleType(vehicleSubType) {
  const sub = Number(vehicleSubType)
  if (sub === VEHICLE_SUBTYPE.OTO) return VEHICLE_TYPE.OTO
  if (sub === VEHICLE_SUBTYPE.MAYDIEN) return VEHICLE_TYPE.MAYDIEN
  return VEHICLE_TYPE.XEMAY
}

function extractVehicleList(res) {
  const listArr = Array.isArray(res?.data?.data || res?.data) ? res?.data?.data || res?.data : []
  return listArr
}

function mapServerVehicleItem(item) {
  const plate = normalizePlate(item?.vehicleIdentity || item?.vehicleIdentityNumber || item?.customerRecordPlatenumber || item?.plate || '')
  if (!plate) return null

  const vehicleSubType = Number(item?.vehicleSubType) || mapVehicleTypeToSubType(Number(item?.vehicleType))
  const vehicleType = Number(item?.vehicleType) || subTypeToVehicleType(vehicleSubType)

  return {
    plate,
    vehicleType,
    vehicleSubType,
    appUserVehicleId: item?.appUserVehicleId,
    activeNotifyPackageIndex: item?.activeNotifyPackageIndex,
    customerSchedule: item?.customerSchedule ?? null,
    vehicleIdentity: item?.vehicleIdentity
  }
}

function loadRecent() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_PLATES_STORAGE_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    const normalized = raw
      .map((x) => {
        if (!x) return null

        if (typeof x === 'object') {
          const plate = normalizePlate(x.plate || '')
          const vehicleType = Number(x.vehicleType)
          if (!plate) return null
          return {
            plate,
            vehicleType: [1, 30, 40].includes(vehicleType) ? vehicleType : 30
          }
        }

        if (typeof x === 'string') {
          const plate = normalizePlate(x)
          if (!plate) return null
          return { plate, vehicleType: 30 }
        }

        return null
      })
      .filter(Boolean)

    const seen = new Set()
    const unique = []
    for (const item of normalized) {
      const key = `${item.plate}-${item.vehicleType}`
      if (seen.has(key)) continue
      seen.add(key)
      unique.push(item)
    }
    return unique.slice(0, 20)
  } catch {
    return []
  }
}

function saveRecent(plate, vehicleType) {
  const p = normalizePlate(plate)
  if (!p) return

  const curr = loadRecent().filter((x) => !(x?.plate === p && Number(x?.vehicleType) === Number(vehicleType)))
  curr.unshift({ plate: p, vehicleType })

  localStorage.setItem(RECENT_PLATES_STORAGE_KEY, JSON.stringify(curr.slice(0, 20)))
}

function removeRecent(plate, vehicleType) {
  const p = normalizePlate(plate)
  const vt = Number(vehicleType)

  const curr = loadRecent().filter((x) => !(x?.plate === p && Number(x?.vehicleType) === vt))
  localStorage.setItem(RECENT_PLATES_STORAGE_KEY, JSON.stringify(curr.slice(0, 20)))
}

function resolveShowPhoneInput(search) {
  const params = new URLSearchParams(search || '')
  const fromParam = smartParseParam(params.get('showPhoneInput'))
  if (fromParam !== null && fromParam !== undefined) {
    sessionStorage.setItem(SHOW_PHONE_INPUT_KEY, String(fromParam))
  }

  const fromSession = smartParseParam(sessionStorage.getItem(SHOW_PHONE_INPUT_KEY))
  const fromEnv = smartParseParam(process.env.REACT_APP_SHOW_PHONE_INPUT ?? process.env.REACT_APP_PARAM_SHOW_PHONE_INPUT)
  const raw = fromParam ?? fromSession ?? fromEnv

  return raw === true || raw === 1
}

export default function TrafficFineLookup() {
  const history = useHistory()
  const location = useLocation()
  const appParams = useAppParamsContext()

  const [commonParams, setCommonParams] = useState({})
  const [phoneResolved, setPhoneResolved] = useState(false)
  const [resolvedPhone, setResolvedPhone] = useState('')
  const [userData, setUserData] = useState(null)
  const [loadingInitData, setLoadingInitData] = useState(false)

  const [phoneInput, setPhoneInput] = useState('')
  const [phoneError, setPhoneError] = useState('')

  const stationId = useMemo(() => Number(commonParams?.stationId || 5146), [commonParams?.stationId])
  const [homeIcons, setHomeIcons] = useState([])
  const [homeBanners, setHomeBanners] = useState([])
  const [homeConfigLoading, setHomeConfigLoading] = useState(false)

  const [view, setView] = useState('HOME')
  const [carType, setCarType] = useState(VehicleTypes.oto)
  const [plateNumber, setPlateNumber] = useState('')
  const [plateError, setPlateError] = useState('')
  const [selectedRecentKey, setSelectedRecentKey] = useState(null)
  const clearSelectedRecent = () => setSelectedRecentKey(null)

  const [recentPlates, setRecentPlates] = useState([])

  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [errorTitle, setErrorTitle] = useState('Tra cứu không thành công')

  const showPhoneInput = useMemo(() => {
    if (typeof appParams?.showPhoneInput === 'boolean') return appParams.showPhoneInput
    return resolveShowPhoneInput(location.search)
  }, [appParams?.showPhoneInput, location.search])
  const shouldShowPhoneInput = showPhoneInput && !resolvedPhone
  // const isHeaderMiniApp = useMemo(() => !!appParams?.isHeaderMiniApp, [appParams?.isHeaderMiniApp])

  useEffect(() => {
    const params = new URLSearchParams(location.search || '')
    setCommonParams(paramsToObject(params))

    const applyPhone = (nextPhoneRaw) => {
      const next = digitsOnly(nextPhoneRaw)
      const prev = digitsOnly(localStorage.getItem(PHONE_STORAGE_KEY)) || ''
      const nextUsername = next ? `${getUsernamePrefix(location.search)}${next}` : ''
      const prevUsername = loadUserData()?.username || ''

      if ((next && prev !== next) || (nextUsername && prevUsername && prevUsername !== nextUsername)) {
        clearAllViolationLookupData()
        setUserData(null)
        setLoadingInitData(false)
      }

      if (next) localStorage.setItem(PHONE_STORAGE_KEY, next)
      else localStorage.removeItem(PHONE_STORAGE_KEY)

      setResolvedPhone(next || '')
      setPhoneResolved(true)
    }

    const fromMsisdn = digitsOnly(params.get('msisdn') || '')
    if (fromMsisdn) {
      applyPhone(fromMsisdn)
      return
    }

    const fromParams = digitsOnly(params.get('phoneNumber') || '')
    if (fromParams) {
      applyPhone(fromParams)
      return
    }

    const fromLS = digitsOnly(localStorage.getItem(PHONE_STORAGE_KEY))
    if (fromLS) {
      applyPhone(fromLS)
      return
    }

    applyPhone('')
  }, [location.search])

  useEffect(() => {
    if (!phoneResolved) return undefined

    if (!resolvedPhone) {
      setLoadingInitData(false)
      setUserData(null)
      return undefined
    }

    let cancelled = false

    const loginByPhone = async (phoneDigits) => {
      const username = `${getUsernamePrefix(location.search)}${phoneDigits}`
      const loginRes = await appUserService.loginUserByPhone({
        phoneNumber: username,
        password: DEFAULT_PASSWORD
      })

      if (!loginRes || loginRes.statusCode !== 200) throw loginRes || new Error('LOGIN_FAILED')

      const data = {
        appUserId: loginRes?.data?.appUserId || loginRes?.data?.id,
        token: loginRes?.data?.token,
        username,
        phoneNumber: phoneDigits,
        updatedAt: new Date().toISOString()
      }

      if (!data.appUserId || !data.token) throw new Error('LOGIN_FAILED')
      saveUserData(data)
      return data
    }

    const registerByPhone = async (phoneDigits) => {
      const username = `${getUsernamePrefix(location.search)}${phoneDigits}`
      const regRes = await appUserService.registerUserByPhone({
        phoneNumber: username,
        password: DEFAULT_PASSWORD
      })
      if (!regRes || regRes.statusCode !== 200) throw regRes || new Error('REGISTER_FAILED')
      return { username }
    }

    ;(async () => {
      setLoadingInitData(true)

      const cached = loadUserData()
      const expectedUsername = `${getUsernamePrefix(location.search)}${resolvedPhone}`
      if (!cached?.phoneNumber || cached.phoneNumber !== resolvedPhone || cached.username !== expectedUsername) {
        localStorage.removeItem(VTM_USER_STORAGE_KEY)
      }

      const cached2 = loadUserData()
      if (cached2?.token && cached2?.appUserId) {
        if (!cancelled) setUserData(cached2)
        if (!cancelled) setLoadingInitData(false)
        return
      }

      try {
        const logged = await loginByPhone(resolvedPhone)
        if (!cancelled) setUserData(logged)
      } catch {
        try {
          await registerByPhone(resolvedPhone)
          const logged2 = await loginByPhone(resolvedPhone)
          if (!cancelled) setUserData(logged2)
        } catch {
          if (!cancelled) setUserData(null)
        }
      } finally {
        if (!cancelled) setLoadingInitData(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [phoneResolved, resolvedPhone, location.search])

  useEffect(() => {
    if (!resolvedPhone) {
      setRecentPlates((prev) => (prev?.length ? prev : loadRecent()))
      return
    }

    if (loadingInitData || !userData?.token || !userData?.appUserId) {
      setRecentPlates((prev) => (prev?.length ? prev : loadRecent()))
      return
    }

    let cancelled = false
    const run = async () => {
      try {
        const res = await appUserService.getListVehicleByAppUserId(userData.appUserId, userData.token, 20)
        const listArr = extractVehicleList(res)

        if (cancelled) return
        setRecentPlates(listArr.map(mapServerVehicleItem).filter(Boolean).slice(0, 20))
      } catch {
        if (!cancelled) setRecentPlates(loadRecent())
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [resolvedPhone, loadingInitData, userData?.token, userData?.appUserId])

  useEffect(() => {
    let alive = true

    setHomeConfigLoading(true)
    const cached = loadHomeCache()
    if (cached) {
      setHomeIcons(cached.homeIcons || [])
      setHomeBanners(cached.homeBanners || [])
      setHomeConfigLoading(false)
      return () => {
        alive = false
      }
    }

    Promise.all([appUserService.getHomePageConfigPartner({}), appUserService.getStationBannersPartner({})])
      .then(([cfgRes, bannerRes]) => {
        if (!alive) return

        const utilities = (Array.isArray(cfgRes?.data?.data) && cfgRes.data.data) || (Array.isArray(cfgRes?.data) && cfgRes.data) || []
        const banners = (Array.isArray(bannerRes?.data?.data) && bannerRes.data.data) || (Array.isArray(bannerRes?.data) && bannerRes.data) || []

        setHomeIcons(utilities)
        setHomeBanners(banners)
        saveHomeCache({ homeIcons: utilities, homeBanners: banners })
      })
      .catch(() => {
        if (!alive) return
        setHomeIcons([])
        setHomeBanners([])
      })
      .finally(() => {
        if (!alive) return
        setHomeConfigLoading(false)
      })

    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    if (view !== 'HOME') return
    if (!resolvedPhone) {
      setRecentPlates(loadRecent())
    }
  }, [view, resolvedPhone])

  const canSubmit = useMemo(() => {
    if (!carType) return false
    if (!plateNumber || !!plateError) return false

    if (shouldShowPhoneInput) {
      const pe = validatePhone(phoneInput, shouldShowPhoneInput)
      if (pe) return false
    }
    return true
  }, [carType, plateNumber, plateError, shouldShowPhoneInput, phoneInput])

  const handleSubmit = async () => {
    const normalized = normalizePlate(plateNumber)
    const err = validatePlateNumber(normalized)
    setPlateError(err)
    if (err) return

    const MAX_VEHICLES = 20
    try {
      const session = userData
      if (session?.token && session?.appUserId) {
        const res = await appUserService.getListVehicleByAppUserId(session.appUserId, session.token, MAX_VEHICLES + 1)
        const listArr = extractVehicleList(res)

        const existsOnServer = listArr.some((v) => normalizePlate(v?.vehicleIdentity || '') === normalized)
        if (!existsOnServer && listArr.length >= MAX_VEHICLES) {
          setErrorTitle('Đã đạt giới hạn lưu xe')
          setErrorMsg(`Bạn đã lưu tối đa ${MAX_VEHICLES} xe.\nVui lòng xoá bớt xe đã lưu để tiếp tục.`)
          setErrorOpen(true)
          setView('HOME')
          return
        }
      }
    } catch {}

    if (shouldShowPhoneInput) {
      const pe = validatePhone(phoneInput, shouldShowPhoneInput)
      setPhoneError(pe)
      if (pe) return
    }

    setView('LOADING')
    const phoneToSend = shouldShowPhoneInput ? phoneInput : resolvedPhone || undefined

    const data = {
      licensePlates: normalized,
      certificateSeries: '',
      vehicleType: carType?.value,
      phoneNumber: phoneToSend ? String(phoneToSend) : undefined
    }

    try {
      const response = await appUserService.userCheckCriminal({ data })
      if (!response || response.statusCode !== 200) throw response || new Error('API_FAIL')

      const criminalData = response?.data || {}
      const checkedAtIso = new Date().toISOString()
      const hasCriminalStatus = Number(criminalData?.hasCriminal)

      if (hasCriminalStatus === HAS_CRIMINAL_STATUS.API_FAIL) {
        const apiMsg = criminalData?.notification || 'Đã xảy ra lỗi khi tra cứu, vui lòng thử lại sau ít phút.'

        setPartnerVehicleResult({
          plate: normalized,
          carTypeValue: carType?.value ?? null,
          carTypeLabel: carType?.label || '',
          checkedAt: checkedAtIso,
          isAPIError: 1,
          isVehicleWarning: 0,
          message: apiMsg,
          phoneNumber: phoneToSend || '',
          stationId: String(stationId || '')
        })
        setView('HOME')
        history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_CHECK_RESULT)
        return
      }

      let warning = false
      let msg = ''

      if (criminalData?.hasCriminal === HAS_CRIMINAL_STATUS.YES) {
        warning = true
        msg = criminalData?.notification
      } else if (criminalData?.isCriminalVR === HAS_CRIMINAL_STATUS.YES) {
        warning = true
        msg = criminalData?.notificationVR
      } else if (criminalData?.isCriminalCSGT === HAS_CRIMINAL_STATUS.YES) {
        warning = true
        msg = criminalData?.notificationCSGT
      }

      const isPhoneMode = !!resolvedPhone
      const isAccount = !!(resolvedPhone && userData?.token && userData?.appUserId)

      if (!isPhoneMode) {
        saveRecent(normalized, carType.value)
        setRecentPlates(loadRecent())
      } else if (isAccount) {
        const session = userData
        const res = await appUserService.getListVehicleByAppUserId(session.appUserId, session.token, MAX_VEHICLES + 1)
        const listArr = extractVehicleList(res)

        const existsOnServer = listArr.some((v) => normalizePlate(v?.vehicleIdentity || '') === normalized)
        if (!existsOnServer && listArr.length >= MAX_VEHICLES) {
          setErrorTitle('Đã đạt giới hạn lưu xe')
          setErrorMsg(`Bạn đã lưu tối đa ${MAX_VEHICLES} xe.\nVui lòng xoá bớt xe đã lưu để tiếp tục.`)
          setErrorOpen(true)
          setView('HOME')
          return
        }

        if (!existsOnServer) {
          try {
            await appUserService.registerVehicle(
              {
                vehicleIdentity: normalized,
                vehiclePlateColor: DEFAULT_PLATE_COLOR,
                vehicleSubType: Number(mapVehicleTypeToSubType(carType.value)),
                vehicleType: Number(carType.value)
              },
              session.token
            )
          } catch {}
        }

        try {
          const res2 = await appUserService.getListVehicleByAppUserId(session.appUserId, session.token, 20)
          const arr2 = extractVehicleList(res2)
          setRecentPlates(arr2.map(mapServerVehicleItem).filter(Boolean).slice(0, 20))
        } catch {}
      } else {
        saveRecent(normalized, carType.value)
        setRecentPlates(loadRecent())
      }

      let matched = null
      let activePkg = null

      setPartnerVehicleResult({
        plate: normalized,
        carTypeValue: carType?.value ?? null,
        carTypeLabel: carType?.label || '',
        checkedAt: checkedAtIso,
        isAPIError: 0,
        isVehicleWarning: warning ? 1 : 0,
        message: msg || '',
        phoneNumber: phoneToSend || '',
        stationId: String(stationId || ''),
        activeNotifyPackageIndex: matched?.activeNotifyPackageIndex ?? null,
        customerSchedule: matched?.customerSchedule ?? null,
        activePkg: activePkg
          ? {
              notifyInfoId: activePkg.appUserVehicleNotifyInfoId,
              productId: activePkg.productId,
              productName: activePkg.productName,
              allowRenew: activePkg.allowRenew,
              notifyStartDateTime: activePkg.notifyStartDateTime,
              notifyEndDateTime: activePkg.notifyEndDateTime,
              appUserVehicleId: activePkg.appUserVehicleId
            }
          : null
      })
      history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_CHECK_RESULT)
    } catch (error) {
      const checkedAtIso = new Date().toISOString()
      const code = error?.response?.data?.error || error?.error
      const cfg = ERROR_MESSAGES?.[code]

      const msg = cfg?.message || 'Đã xảy ra một vài sự cố kỹ thuật, vui lòng thử lại sau ít phút.'

      setPartnerVehicleResult({
        plate: normalized,
        carTypeValue: carType?.value ?? null,
        carTypeLabel: carType?.label || '',
        checkedAt: checkedAtIso,
        isAPIError: 1,
        isVehicleWarning: 0,
        message: msg,
        phoneNumber: phoneToSend || '',
        stationId: String(stationId || '')
      })
      setView('HOME')
      history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_CHECK_RESULT)
    }
  }

  if (!phoneResolved) return null

  if (resolvedPhone && (loadingInitData || !userData)) {
    return (
      <LayoutPartner>
        <div className="TrafficFineLookupBrandLoading">
          <div className="TrafficFineLookupBrandLoading-spinner" />
          <img src={'/logo.png'} alt="loading" />
        </div>
      </LayoutPartner>
    )
  }

  return (
    <LayoutPartner>
      <HeaderPartner title={'Tra cứu phạt nguội'} />
      <div className="TrafficFineLookup">
        <div className="traffic-fine-hero-bg" style={{ backgroundImage: `url(${trafficFineBgHero})` }} />

        <div className="traffic-fine-content" style={{ paddingTop: '20px' }}>
          {/* Legacy header logic (kept for reference)
          {isHeaderMiniApp ? <ViettelMoneyHeader onBack={() => history.goBack()} /> : <div className="traffic-fine-header-space" />}
          */}
          <AutomatedTrafficFineNotificationErrorModal open={errorOpen} title={errorTitle} content={errorMsg} onClose={() => setErrorOpen(false)} />

          <div className={view === 'LOADING' ? 'traffic-fine-main is-disabled' : 'traffic-fine-main'}>
            <VehicleSearchCard
              carType={carType}
              vehicleTypes={VehicleTypes}
              vehicleIcons={VehicleIcons}
              plateNumber={plateNumber}
              plateError={plateError}
              canSubmit={canSubmit}
              showPhoneInput={shouldShowPhoneInput}
              phoneNumber={phoneInput}
              phoneError={phoneError}
              onPhoneChange={(v) => {
                setPhoneInput(v)
                setPhoneError(validatePhone(v, shouldShowPhoneInput))
              }}
              onClearSelectedRecent={clearSelectedRecent}
              onCarTypeChange={(v) => {
                setCarType(v)
              }}
              onPlateChange={(raw) => {
                const v = sanitizePlateInput(raw)
                setPlateNumber(v)
                setPlateError(validatePlateNumber(v))
              }}
              onSubmit={handleSubmit}
            />

            {recentPlates.length > 0 ? (
              <RecentVehicleList
                items={recentPlates}
                phoneNumber={resolvedPhone}
                vehicleTypeIconMap={VehicleTypeIconMap}
                vehicleTypesByValue={VehicleTypesByValue}
                selectedKey={selectedRecentKey}
                onToggleSelect={(key) => {
                  setSelectedRecentKey(key)

                  if (key === null) {
                    setPlateNumber('')
                    setPlateError('')
                    return
                  }

                  const [plateRaw, vtRaw] = String(key).split('-')
                  const plate = normalizePlate(plateRaw || '')
                  const vt = Number(vtRaw)

                  const item = recentPlates.find((x) => normalizePlate(x.plate) === plate && Number(x.vehicleType) === vt)

                  if (item) {
                    const v = normalizePlate(item.plate)
                    setPlateNumber(v)
                    setPlateError(validatePlateNumber(v))

                    const picked = Object.values(VehicleTypes).find((x) => x.value === item.vehicleType)
                    if (picked) setCarType(picked)
                  }
                }}
                onDelete={async (item) => {
                  const plate = normalizePlate(item.plate)
                  const vt = Number(item.vehicleType)

                  const activePkg = getActiveNotifyPkgByIndex(item)
                  if (activePkg) {
                    setErrorTitle('Không thể xoá xe')
                    setErrorMsg('Xe đang có gói thông báo còn hiệu lực\nVui lòng không xoá xe.')
                    setErrorOpen(true)
                    return
                  }

                  try {
                    const session = userData

                    if (!session?.token || !session?.appUserId) {
                      removeRecent(plate, vt)
                      setRecentPlates((prev) => prev.filter((x) => !(normalizePlate(x.plate) === plate && Number(x.vehicleType) === vt)))
                      return
                    }

                    const id = item?.appUserVehicleId
                    if (!id) throw new Error('MISSING_VEHICLE_ID')

                    const delRes = await appUserService.deleteVehicle(id, session.token)
                    if (!delRes || delRes.statusCode !== 200) throw delRes || new Error('DELETE_FAILED')

                    const res2 = await appUserService.getListVehicleByAppUserId(session.appUserId, session.token, 20)
                    const arr2 = extractVehicleList(res2)
                    setRecentPlates(arr2.map(mapServerVehicleItem).filter(Boolean).slice(0, 20))
                  } catch (e) {
                    const code = e?.response?.data?.error || e?.error
                    if (code === 'ERROR_NOTIFY_PACKAGE_STILL_ACTIVE') {
                      setErrorTitle('Không thể xoá xe')
                      setErrorMsg('Xe đang có gói thông báo còn hiệu lực. Vui lòng không xoá xe.')
                      setErrorOpen(true)
                      return
                    }
                    setErrorTitle('Không thể xoá xe')
                    setErrorMsg('Đã xảy ra lỗi, vui lòng thử lại sau.')
                    setErrorOpen(true)
                  }
                }}
              />
            ) : (
              <EmptyRecentCard />
            )}

            <div className="traffic-fine-benefits-card">
              <div className="traffic-fine-benefits-card-notch-top" />
              <div className="traffic-fine-benefits-card-notch-bottom" />

              <div className="traffic-fine-benefits-card-title">Ưu đãi cho bạn</div>

              <img src={trafficFineBenefitCar} aria-hidden alt="" className="traffic-fine-benefits-car-image" />

              <div className="traffic-fine-benefits-card-content">
                <BottomBenefitsSection promoBanners={homeBanners} loading={homeConfigLoading} />
                <div style={{ margin: '0 16px' }}>
                  <ExpandedUtilitiesSection
                    utilityIcon={trafficFineUtilityPlaceholder}
                    utilities={homeIcons.map((it) => ({
                      key: it.stationHomePageConfigId,
                      label: it.title,
                      icon: it.imageUrl,
                      linkNavigation: it.linkNavigation,
                      navigationType: it.navigationType
                    }))}
                    loading={homeConfigLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {view === 'LOADING' ? (
          <div className="traffic-fine-loading-backdrop">
            <div className="traffic-fine-loading-circle-wrap">
              <div className="traffic-fine-loading-circle" />
              <img src={'/logo.png'} alt="loading" />
            </div>
          </div>
        ) : null}
      </div>
    </LayoutPartner>
  )
}
